<template>
  <div class="hpsfir-card goods">
    <div class="goods-container">
      <div class="category-nav">
        <el-menu
          :default-active="String(selectedCategoryId)"
          @select="handleCategorySelect"
        >
          <el-menu-item index="0">
            <span>全部分类</span>
          </el-menu-item>
          <el-menu-item index="-1">
            <span>未分类</span>
          </el-menu-item>
          <el-menu-item
            v-for="category in categoryOptions"
            :key="category.value"
            :index="String(category.value)"
          >
            <span>{{ category.label }}</span>
          </el-menu-item>
        </el-menu>
      </div>
      <div class="goods-content">
        <div class="header-actions">
          <el-tabs v-model="activeTab" @tab-change="handleTabChange" class="flex-1">
            <el-tab-pane label="已上架" name="onSale" />
            <el-tab-pane label="仓库中" name="offSale" />
          </el-tabs>
          <el-button type="primary" @click="openCreateDialog">创建商品</el-button>
        </div>
        <HpsfirProTable
          ref="tableRef"
          :paginations="{ pageSize: 10 }"
          page-layout="total, sizes, prev, pager, next"
          :columns="columns"
          :get-data="getData"
          :on-delete="deleteFun"
          :loading="loading"
          :is-dangder-delete="true"
          :format-dangder-delete-info="formatDeleteInfo"
          row-key="id"
          :operates="operates"
          :codition-init-values="conditionsInitValue"
          :hide-create="true"
          :hide-edit="true"
        />
      </div>
    </div>

    <GoodsEditorDialog
      v-model:visible="editorVisible"
      :mode="editorMode"
      :initial-value="editorInitialValue"
      :category-options="categoryOptions"
      :upload-url="useApi().uploadFileUrl"
      :upload-headers="uploadHeaders"
      :base-url="useApi().baseUrl"
      :submitting="editorSubmitting"
      @submit="submitGoods"
    />
  </div>
</template>
<script lang="tsx" setup>
import {
  HpsfirProTable,
  HpsfirProTableColumn,
  HpsfirProTableData,
  HpsfirProTableFilter,
  HpsfirProTableGetDataFunction,
  useBaseAsync,
  useBaseUserStore,
} from 'hpsfir-managebase-ui';
import { useApi } from '@/hooks/useApi';
import { ElMessage } from 'element-plus';
import { ref, computed } from 'vue';
import GoodsEditorDialog from './components/goods-editor-dialog.vue';

const columns = ref<HpsfirProTableColumn[]>([]);
const tableRef = ref<InstanceType<typeof HpsfirProTable>>();
const categoryOptions = ref<{ label: string; value: number }[]>([]);
const selectedCategoryId = ref<number>(0);
const baseUserStore = useBaseUserStore();
const editorVisible = ref(false);
const editorMode = ref<'create' | 'edit'>('create');
const editorSubmitting = ref(false);
const editingGoods = ref<Record<string, any> | undefined>();


const addInitValue = computed(() => ({
  isOnSale: true,
  sequence: 0,
  stock: 0,
  giftPoints: 0,
  specs: [],
  ...(selectedCategoryId.value ? { categoryId: selectedCategoryId.value } : {}),
}));

const editorInitialValue = computed(() => {
  if (editorMode.value === 'create') {
    return addInitValue.value;
  }
  return editingGoods.value;
});

const uploadHeaders = computed(() => ({
  Authorization: baseUserStore.token,
}));

const conditionsInitValue = {
};

// 加载分类选项
const loadCategoryOptions = useBaseAsync(async() => {
  const res = await useApi().categoryApi.listCategorycategory({
    conditions: {},
  });
  categoryOptions.value = (res.data.data || []).map((item: any) => ({
    label: item.name,
    value: item.id,
  }));
});

// 创建列定义
// eslint-disable-next-line max-lines-per-function
const createColumns = () => [
  {
    name: 'sequence',
    label: '排序',
    type: 'number',
    min: 0,
    step: 1,
    width: 80,
  },
  {
    name: 'categoryId',
    label: '分类',
    type: 'select',
    hideTable: true,
    options: categoryOptions.value,
    rule: [{ required: true, message: '请选择分类' }],
    width: 120,
  },
  {
    name: 'image',
    label: '图片',
    type: 'image',
    rule: [{ required: true, message: '请上传图片' }],
    action: useApi().uploadFileUrl,
    method: 'post',
    fieldName: 'files',
    headers: {
      Authorization: baseUserStore.token,
    },
    baseUrl: useApi().baseUrl,
    multiple: false,
    onSuccess(data) {
      console.log(data.data[0]);
      tableRef.value?.tableForm?.setOneFormState('image', data.data[0]);
    },
    width: 200,
  },
  {
    name: 'name',
    label: '名称',
    type: 'string',
    isCondition: true,
    rule: [{ required: true, message: '请输入名称' }],
    width: 150,
  },
  {
    name: 'description',
    label: '描述',
    type: 'string',
    rule: [{ required: true, message: '请输入描述' }],
    width: 200,
  },
  {
    name: 'price',
    label: '价格',
    type: 'string',
    rule: [
      { required: true, message: '请输入价格' },
      {
        pattern: /^\d+(?:\.\d{1,2})?$/u,
        message: '价格格式不正确，最多保留两位小数',
      },
    ],
    width: 100,
    render(scope) {
      if (scope.row.isPointsExchange) {
        return (<span>{scope.row.price} 积分</span>);
      }
      return (<span>¥ {scope.row.price}</span>);
    },
  },
  {
    name: 'isPointsExchange',
    label: '积分兑换商品',
    type: 'swtich',
    width: 120,
    render(scope) {
      return scope.row.isPointsExchange
        ? <el-tag type="warning">积分兑换</el-tag>
        : <el-tag type="info">普通商品</el-tag>;
    },
  },
  {
    name: 'specs',
    label: '规格',
    type: 'string',
    width: 180,
    render(scope) {
      const specs = scope.row.specs || [];
      if (!specs.length) {
        return (<span>-</span>);
      }
      return (<span>{specs.map((item: any) => item.name).join(' / ')}（{specs.length}组）</span>);
    },
  },
  {
    name: 'giftPoints',
    label: '赠送积分',
    type: 'number',
    min: 0,
    step: 1,
    width: 100,
  },
  {
    name: 'stock',
    label: '库存',
    type: 'number',
    rule: [{ required: true, message: '请输入库存' }],
    min: 0,
    step: 1,
    width: 140,
    render(scope) {
      return (
        <div class="stock-cell">
          <span>{scope.row.stock}</span>
          {scope.row.stock < 10 && <el-tag type="danger" size="small">库存紧张</el-tag>}
        </div>
      );
    },
  },
  {
    name: 'isOnSale',
    label: '是否上架',
    type: 'swtich',
    hideTable: true,
    width: 100,
  },
  {
    name: 'createTime',
    label: '创建时间',
    type: 'string',
    hideEdit: true,
    hideCreate: true,
    width: 180,
  },
] as HpsfirProTableColumn[];

// 初始化
const init = useBaseAsync(async() => {
  await loadCategoryOptions();
  columns.value = createColumns();
});

// 上架操作
const handleOnSale = useBaseAsync(async(row: any) => {
  await useApi().goodsApi.updateGoodsgoods({
    goodsAdminVo: {
      ...row,
      isOnSale: true,
    },
  });
  ElMessage.success('上架成功');
  tableRef.value?.refresh();
});

// 下架操作
const handleOffSale = useBaseAsync(async(row: any) => {
  await useApi().goodsApi.updateGoodsgoods({
    goodsAdminVo: {
      ...row,
      isOnSale: false,
    },
  });
  ElMessage.success('下架成功');
  tableRef.value?.refresh();
});

const operates = [
  {
    key: 'customEdit',
    name: '编辑',
    handler: (row: any) => {
      editorMode.value = 'edit';
      editingGoods.value = {
        ...row,
        specs: row.specs || [],
      };
      editorVisible.value = true;
    },
  },
  {
    key: 'onSale',
    name: '上架',
    handler: handleOnSale,
  },
  {
    key: 'offSale',
    name: '下架',
    handler: handleOffSale,
  },
];

const loading = ref(false);
const activeTab = ref<'onSale' | 'offSale'>('onSale');

// 切换标签时刷新表格
const handleTabChange = () => {
  tableRef.value?.refresh();
};

// 切换分类
const handleCategorySelect = (index: string) => {
  selectedCategoryId.value = Number(index);
  tableRef.value?.refresh();
};

const getData = useBaseAsync<HpsfirProTableGetDataFunction>(async(filter: HpsfirProTableFilter) => {
  try {
    loading.value = true;
    const res = await useApi().goodsApi.pageGoodsgoods({
      conditions: Object.entries(filter.conditions).reduce((conditions, [key, val]) => {
        if (val === null || val === undefined || val === '') {
          return conditions;
        }

        conditions[key] = val;

        return conditions;
      }, {
        isOnSale: activeTab.value === 'onSale',
        ...(selectedCategoryId.value === 0 ? {} : { categoryId: selectedCategoryId.value }),
      } as any),
      pageQuery: {
        page: filter.paginations.page - 1,
        pageSize: filter.paginations.pageSize,
      },
    });

    const tableData: HpsfirProTableData = {
      total: res.data.data?.total || 0,
      data: (res.data.data?.data || []).map((item: any) => ({
        ...item,
        hideOp: {
          onSale: item.isOnSale,
          offSale: !item.isOnSale,
        },
      })),
    };
    return tableData;
  } finally {
    loading.value = false;
  }
});

const formatDeleteInfo = (row: any) => `【删除后，并不影响已经获取的商品券与订单，但会影响券类型中绑定的商品】您确定要删除商品"${row.name || '未命名'}"吗？此操作不可恢复，请谨慎操作`;

function openCreateDialog() {
  editorMode.value = 'create';
  editingGoods.value = undefined;
  editorVisible.value = true;
}

function openEditDialog(row: any) {
  editorMode.value = 'edit';
  editingGoods.value = {
    ...row,
    specs: row.specs || [],
  };
  editorVisible.value = true;
}

const submitGoods = useBaseAsync(async(data: any) => {
  try {
    editorSubmitting.value = true;
    const payload = {
      ...data,
    };
    if (editorMode.value === 'create') {
      await useApi().goodsApi.addGoodsgoods({
        goodsAdminVo: payload,
      } as any);
      ElMessage.success('添加成功');
    } else {
      await useApi().goodsApi.updateGoodsgoods({
        goodsAdminVo: payload,
      } as any);
      ElMessage.success('修改成功');
    }
    editorVisible.value = false;
    tableRef.value?.refresh();
  } finally {
    editorSubmitting.value = false;
  }
});

const deleteFun = useBaseAsync(async(data: any) => {
  await useApi().goodsApi.delGoodsgoods(data.id);
  ElMessage.success('删除成功');
});

Promise.all([init()]);
</script>
<style lang="scss" scoped>
@import './index.scss';

.goods-container {
  display: flex;
  gap: 16px;
}

.category-nav {
  width: 160px;
  flex-shrink: 0;
  border-right: 1px solid #e4e7ed;

  .el-menu {
    border-right: none;
  }
}

.goods-content {
  flex: 1;
  min-width: 0;
}

.header-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;

  .flex-1 {
    flex: 1;
  }
}

:deep(.stock-cell) {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
